import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, CheckCircle, Users, Baby, DollarSign, Monitor, Mail, MapPin, Heart, Brain, Clock, FileText, Shield, ArrowRight, Target } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const ADHDAssessmentPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const targetGroups = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'ADHD Assessments for Women',
      description: 'Many women go undiagnosed because ADHD often presents differently in females. Symptoms like inattention, chronic overwhelm, and emotional dysregulation are frequently mistaken for anxiety or depression. Our assessments are designed to recognise these gender-specific presentations.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'ADHD Assessments for Men',
      description: 'Whether you\'ve struggled with focus and impulsivity your whole life or are seeking answers after years of frustration, our comprehensive assessments for men and boys use evidence-based diagnostic tools to provide clarity, understanding, and a path forward.'
    },
    {
      icon: <Baby className="w-8 h-8" />,
      title: 'ADHD Assessments for Children',
      description: 'Early identification of ADHD can significantly improve outcomes for children. Our child assessments are developmentally appropriate and conducted in close collaboration with parents, teachers, and caregivers to build a complete picture of your child\'s strengths and challenges.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Adult ADHD Assessments',
      description: 'Many adults seek an ADHD assessment after years of struggling with organisation, time management, or relationships without understanding why. We specialise in adult ADHD diagnosis, understanding the unique challenges of identifying ADHD later in life.'
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: 'Telehealth Assessments Australia-Wide',
      description: 'Our assessments are fully conducted via secure telehealth video sessions, making a professional ADHD assessment accessible from anywhere in Australia — no travel required.'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Medicare Rebates & Flexible Payment Plans',
      description: 'Medicare rebates and flexible payment plans are available, and we support clients with NDIS funding, to help make assessments more accessible. Contact us to discuss your funding situation and what rebates may apply to you.'
    }
  ];

  const process = [
    { step: '1', title: 'Initial Enquiry', description: 'Contact us to discuss your concerns and needs. We will help you understand whether an ADHD assessment is the right step and answer any questions about the process, costs, and timelines.' },
    { step: '2', title: 'Pre-Assessment Questionnaires', description: 'Before your first session, you will complete background questionnaires covering your developmental history, current symptoms, and daily functioning. For children, parents and teachers may also complete ADHD rating scales.' },
    { step: '3', title: 'Clinical Interview', description: 'An in-depth telehealth interview with our psychologist covering your developmental history, symptom presentation (inattention, hyperactivity, impulsivity), and how these impact your work, relationships, and daily life.' },
    { step: '4', title: 'Standardised Assessments', description: 'We use evidence-based diagnostic tools including standardised ADHD rating scales and clinical observations. Collateral information from family members, partners, or teachers is also gathered when appropriate.' },
    { step: '5', title: 'Comprehensive Report', description: 'You receive a detailed written report with diagnostic conclusions, assessment results, identified strengths and challenges, and personalised recommendations for support, accommodations, and next steps.' },
    { step: '6', title: 'Feedback Session', description: 'A dedicated session to walk through your results, discuss what the findings mean for you, and answer any questions. We provide practical strategies and connect you with appropriate supports, including therapy or medication referrals if needed.' }
  ];

  const faqs = [
    {
      question: 'What is an ADHD assessment and who is it for?',
      answer: 'An ADHD (Attention-Deficit/Hyperactivity Disorder) assessment is a comprehensive evaluation conducted by a registered psychologist to determine whether an individual meets the diagnostic criteria for ADHD. Our assessments are available for adults, adolescents, and children of all genders. Whether you are a woman struggling with chronic overwhelm and inattention, a man seeking answers for lifelong impulsivity and focus issues, or a parent concerned about your child — our assessments are tailored to your specific situation.'
    },
    {
      question: 'How long does an ADHD assessment take?',
      answer: 'A full ADHD assessment typically involves 3 to 5 hours of clinical time, spread across multiple telehealth sessions over several weeks. This includes completing pre-assessment questionnaires, a detailed clinical interview, standardised ADHD rating scales, collateral interviews where appropriate, and a feedback session. Spreading the assessment across sessions ensures thoroughness without fatigue.'
    },
    {
      question: 'Can ADHD assessments really be done via telehealth?',
      answer: 'Yes. Telehealth ADHD assessments are supported by current research and follow the same rigorous clinical standards as in-person assessments. Our psychologist uses validated assessment tools that are approved for telehealth delivery. Telehealth also offers benefits including the comfort of being assessed in your own environment, reduced sensory overwhelm, and access for Australians in regional and rural areas.'
    },
    {
      question: 'Do you assess adults for ADHD?',
      answer: 'Absolutely. A growing number of adults are seeking ADHD assessments, often after years of struggling without understanding why. Many adults — particularly women — were missed as children because their symptoms did not match outdated stereotypes or were dismissed as anxiety or personality traits. We have specific expertise in adult ADHD diagnosis and understanding how ADHD presents across the lifespan.'
    },
    {
      question: 'How is ADHD different in women and girls?',
      answer: 'ADHD in women and girls often looks different from the traditional male presentation that diagnostic criteria were originally based on. Women are more likely to present with inattention rather than hyperactivity, experience chronic overwhelm and mental clutter, develop sophisticated coping strategies that mask their struggles, and internalise their difficulties (presenting as anxiety, depression, or low self-esteem). Our assessments specifically account for these gender differences.'
    },
    {
      question: 'Do you assess children for ADHD?',
      answer: 'Yes, we assess children of all ages. For younger children, the assessment process involves close collaboration with parents, caregivers, and often teachers. We gather information from multiple sources to build a comprehensive understanding of your child\'s attention, impulse control, and activity levels across different environments. Our approach is child-centred, supportive, and designed to be as comfortable as possible.'
    },
    {
      question: 'Do I need a GP referral for an ADHD assessment?',
      answer: 'A GP referral is not required to book an ADHD assessment with us. However, if you wish to access Medicare rebates, you will need a Mental Health Care Plan (MHCP) from your GP, which provides a set number of rebated sessions. We can guide you through this process. NDIS participants can also use their funding — contact us to discuss your plan.'
    },
    {
      question: 'Are Medicare rebates or payment plans available?',
      answer: 'Yes. We support clients with NDIS funding and Medicare rebates. If you have a Mental Health Care Plan from your GP, you may be eligible for Medicare rebates on sessions. NDIS participants can use their plan funding for assessments. Flexible payment plans are also available to help spread the cost of your assessment. Contact us to discuss your specific funding situation and we will help you understand your options.'
    },
    {
      question: 'Will I receive a formal diagnosis and written report?',
      answer: 'Yes. If diagnostic criteria are met, you will receive a formal ADHD diagnosis supported by a comprehensive written report. The report includes a summary of assessment findings, diagnostic conclusions, identified strengths, areas of challenge, and personalised recommendations for support, workplace accommodations, educational adjustments, medication consultation, or therapeutic pathways. This report can be used for NDIS applications, workplace disclosure, educational support, or personal understanding.'
    },
    {
      question: 'What happens after I receive my diagnosis?',
      answer: 'After your assessment, we provide a feedback session to discuss your results in detail and answer any questions. Depending on your needs, we can help connect you with appropriate supports including therapy (we offer ongoing telehealth therapy), medication consultation referrals to GPs or psychiatrists, NDIS plan development support, workplace or educational accommodations guidance, and community resources. A diagnosis is the beginning of a journey toward better self-understanding, not an endpoint.'
    },
    {
      question: 'Can you assess for both ASD and ADHD together?',
      answer: 'Yes. Many individuals have co-occurring ASD and ADHD, and we offer combined assessments that evaluate both conditions. This is especially important because the symptoms of ASD and ADHD can overlap and interact in complex ways. A combined assessment ensures that nothing is missed and that recommendations address the full picture.'
    },
    {
      question: 'What areas of Australia do you cover?',
      answer: 'Because our assessments are conducted entirely via telehealth, we can see clients anywhere in Australia. We regularly work with clients in Sydney, Melbourne, Brisbane, Perth, Adelaide, Hobart, Darwin, Canberra, the Gold Coast, and regional and remote areas across all states and territories. All you need is a stable internet connection and a private space for your sessions.'
    },
    {
      question: 'Can I use my ADHD diagnosis report to get medication?',
      answer: 'Yes. Our comprehensive diagnostic reports are suitable for GP or psychiatrist consultation regarding medication options if that is part of your treatment plan. Many adults and adolescents with ADHD benefit from a combination of medication and therapy. We can provide referrals to appropriate medical professionals who specialise in ADHD medication management.'
    },
    {
      question: 'What is the difference between ADHD-Inattentive, ADHD-Hyperactive, and ADHD-Combined?',
      answer: 'ADHD has three presentations: ADHD-Predominantly Inattentive (difficulty focusing, easily distracted, forgetfulness), ADHD-Predominantly Hyperactive-Impulsive (restlessness, impulsivity, difficulty waiting), and ADHD-Combined (symptoms of both inattention and hyperactivity/impulsivity). Our assessment will identify which presentation applies to you, as this can guide treatment recommendations.'
    }
  ];

  return (
    <>
      <SEO
        title="ADHD Assessment Online Australia | Adults, Children, Women & Men"
        description="Comprehensive telehealth ADHD assessments for adults, children, women, and men across Australia. Professional ADHD diagnosis with Medicare rebates and flexible payment plans available. Detailed reports and personalised support."
        keywords="ADHD assessment Australia, ADHD assessment online, ADHD diagnosis telehealth, adult ADHD assessment, ADHD assessment women, ADHD assessment children, NDIS ADHD assessment, Medicare ADHD assessment, ADHD assessment Brisbane, ADHD assessment Melbourne, ADHD assessment Sydney, telehealth ADHD assessment Australia"
        url="/adhd-assessment"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                <span style={{ color: 'var(--blue-primary)' }}>ADHD Assessments</span> Online Across Australia
              </h1>
              <p className="text-lg md:text-xl mb-4" style={{ color: 'var(--text-secondary)' }}>
                Comprehensive ADHD assessments for adults, children, women, and men — fully conducted via secure telehealth from anywhere in Australia.
              </p>
              <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
                Medicare rebates and flexible payment plans available. Evidence-based approach.
              </p>
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="px-8 py-4 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:opacity-90 transition-all duration-300 hover:scale-105 inline-block"
              >
                Enquire About an Assessment
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Who Is This Assessment For - AI Optimised Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Who Is an ADHD Assessment For?
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                An ADHD assessment may be right for you or your child if you have noticed persistent difficulties with attention, impulse control, organisation, or hyperactivity that impact daily functioning. Many of our clients come to us after years of struggling without understanding why.
              </p>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Our assessments are suitable for:
              </p>
              <ul className="space-y-3 text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Adults</strong> who suspect they may have ADHD, including those who were never assessed as children or received a different diagnosis such as anxiety or depression</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Women and girls</strong> whose ADHD may have been missed due to inattentive presentations, masking strategies, or symptoms dismissed as anxiety or emotional dysregulation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Men and boys</strong> seeking a comprehensive, evidence-based assessment conducted by an experienced psychologist</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Children and adolescents</strong> whose parents, teachers, or paediatricians have identified concerns with attention, impulsivity, or hyperactivity</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Individuals seeking a combined ASD and ADHD assessment</strong>, as these conditions frequently co-occur and benefit from being assessed together</span>
                </li>
              </ul>
              <p className="text-base" style={{ color: 'var(--text-muted)' }}>
                No GP referral is required to book. However, a Mental Health Care Plan from your GP is needed to access Medicare rebates.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Assessment Types Cards */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Specialised ADHD Assessments for Every Individual
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                ADHD presents differently across genders, ages, and individuals. Our assessments are tailored to recognise these unique presentations and ensure nothing is missed.
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {targetGroups.map((group, index) => (
              <FadeInUp key={group.title} delay={index * 0.1}>
                <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    {group.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                    {group.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {group.description}
                  </p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* How Does a Telehealth Assessment Work */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                How Does a Telehealth ADHD Assessment Work?
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                Our ADHD assessments are conducted entirely via secure video telehealth sessions, following the same rigorous clinical standards as in-person assessments. Current research supports the validity and reliability of telehealth ADHD assessments when conducted by experienced professionals using validated tools.
              </p>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Telehealth assessments offer several benefits: you can complete your assessment from the comfort and familiarity of your own home, there is reduced sensory overwhelm compared to unfamiliar clinical settings, and it removes travel barriers — making professional ADHD assessments accessible to Australians in every state and territory, including regional and remote areas.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Process Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                What Does the ADHD Assessment Process Involve?
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Our comprehensive six-step process is designed to be thorough, supportive, and conducted at a pace that works for you.
              </p>
            </div>
          </FadeInUp>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {process.map((item, index) => (
                <FadeInUp key={item.step} delay={index * 0.1}>
                  <div className="bg-white p-6 rounded-xl shadow-md flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[var(--blue-primary)] text-white rounded-full flex items-center justify-center text-xl font-bold">
                      {item.step}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                        {item.title}
                      </h3>
                      <p style={{ color: 'var(--text-secondary)' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect / Assessment Includes Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <FadeInUp>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  What Is Included in Your ADHD Assessment?
                </h2>
                <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Our assessments use evidence-based diagnostic tools and are conducted by an experienced registered psychologist. We take a compassionate, thorough approach that recognises the real-world impact of ADHD on your life.
                </p>
                <div className="bg-[var(--gray-100)] p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Every assessment includes:</h3>
                  <ul className="space-y-2">
                    {[
                      'Comprehensive developmental and symptom history review',
                      'Validated ADHD assessment tools and rating scales',
                      'Clinical observation and structured interview',
                      'Collateral information from family, partners, or teachers where appropriate',
                      'Evaluation of symptom impact on work, education, relationships, and daily functioning',
                      'Detailed written diagnostic report with personalised recommendations',
                      'Dedicated feedback session to discuss your results',
                      'Guidance on next steps including therapy, medication consultation, NDIS, workplace accommodations, or educational support'
                    ].map((item) => (
                      <li key={item} className="flex items-start">
                        <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--blue-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <div className="relative">
                <img 
                  src="/images/adhd-runner.png" 
                  alt="ADHD assessment illustration showing the comprehensive and supportive nature of our ADHD evaluation process"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* What Happens After Your Diagnosis */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                What Happens After Your ADHD Assessment?
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Receiving a diagnosis is the beginning of a journey toward better self-understanding and effective strategies, not an endpoint. After your assessment, we support you with:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: <FileText className="w-6 h-6" />, title: 'Comprehensive Report', description: 'A detailed written report you can share with your GP, employer, school, psychiatrist, or NDIS planner as needed.' },
                  { icon: <Heart className="w-6 h-6" />, title: 'Ongoing Therapy', description: 'We offer continued telehealth therapy to support your wellbeing, develop practical strategies, and improve daily functioning after diagnosis.' },
                  { icon: <Shield className="w-6 h-6" />, title: 'Medication Referrals', description: 'Your report can be used for GP or psychiatrist consultations regarding ADHD medication. We can provide referrals to medical professionals who specialise in ADHD medication management.' },
                  { icon: <Brain className="w-6 h-6" />, title: 'Personalised Strategies', description: 'Practical recommendations tailored to your life — whether that means workplace accommodations, educational adjustments, time management tools, or daily coping strategies.' },
                ].map((item) => (
                  <div key={item.title} className="bg-white p-6 rounded-xl shadow-md">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Australia-Wide Coverage */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-6">
                <MapPin className="w-8 h-8" style={{ color: 'var(--blue-primary)' }} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                ADHD Assessments Available Across All of Australia
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                Because our assessments are conducted entirely via telehealth, we work with clients in every state and territory. You do not need to travel to a clinic or live near a specialist — all you need is a stable internet connection and a quiet, private space.
              </p>
              <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
                We regularly see clients in Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Hobart, Darwin, Canberra, and regional areas across New South Wales, Victoria, Queensland, Western Australia, South Australia, Tasmania, Northern Territory, and the ACT.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Hobart', 'Darwin', 'Canberra', 'Regional Australia'].map((city) => (
                  <span key={city} className="px-4 py-2 bg-[var(--blue-pale)] rounded-full text-sm font-medium" style={{ color: 'var(--navy-primary)' }}>
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Related Services */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                Related Services
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/asd-assessment" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    ASD Assessment
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Comprehensive ASD assessments for adults and children via telehealth. Combined ASD and ADHD assessments are also available.
                  </p>
                </Link>
                <Link to="/therapy" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    Telehealth Therapy
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Ongoing evidence-based therapy to support your wellbeing after diagnosis, including ADHD coaching, executive function support, and strategies for daily life.
                  </p>
                </Link>
                <Link to="/contact" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Have questions about whether an ADHD assessment is right for you? Contact us to discuss your situation — no obligation.
                  </p>
                </Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
                Frequently Asked Questions About ADHD Assessments
              </h2>
              <p className="text-lg mb-12 text-center" style={{ color: 'var(--text-secondary)' }}>
                Answers to the most common questions we receive about ADHD assessments for adults, children, women, and men.
              </p>
            </FadeInUp>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FadeInUp key={index} delay={index * 0.05}>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      data-testid={`faq-button-${index}`}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-[var(--gray-100)] transition-colors"
                    >
                      <span className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                        {faq.question}
                      </span>
                      <ChevronDown
                        className={`transform transition-transform flex-shrink-0 ml-4 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                        style={{ color: 'var(--blue-primary)' }}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 py-4 border-t" style={{ color: 'var(--text-secondary)' }}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                </FadeInUp>
              ))}
            </div>

            {/* FAQ Schema JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}} />

            {/* Service Schema JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "MedicalProcedure",
              "name": "ADHD (Attention-Deficit/Hyperactivity Disorder) Assessment",
              "description": "Comprehensive telehealth ADHD assessment for adults, children, women, and men across Australia. Includes clinical interview, standardised ADHD rating scales, detailed diagnostic report, and feedback session.",
              "procedureType": "https://schema.org/DiagnosticProcedure",
              "howPerformed": "Conducted via secure telehealth video sessions",
              "preparation": "Pre-assessment questionnaires and developmental history forms",
              "followup": "Feedback session, comprehensive written report, and ongoing therapy or medication referral options",
              "status": "https://schema.org/EventScheduled",
              "availableService": {
                "@type": "MedicalTherapy",
                "name": "Post-Assessment Telehealth Therapy and ADHD Coaching"
              }
            })}} />

            {/* BreadcrumbList Schema */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.navigatingminds.com.au/" },
                { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.navigatingminds.com.au/services" },
                { "@type": "ListItem", "position": 3, "name": "ADHD Assessment", "item": "https://www.navigatingminds.com.au/adhd-assessment" }
              ]
            })}} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Start Your ADHD Assessment?
              </h2>
              <p className="text-lg mb-4 opacity-90">
                Take the first step toward understanding yourself or your child better. Contact us to discuss your needs and book your assessment.
              </p>
              <p className="text-base mb-8 opacity-80">
                Medicare rebates and flexible payment plans available. No GP referral required to enquire.
              </p>
              <Link
                to="/contact"
                data-testid="adhd-cta-button"
                className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-block"
              >
                Book Your Assessment
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Sticky Mobile Contact Button */}
      <Link
        to="/contact#contact-form"
        className="md:hidden fixed bottom-6 right-6 z-50 bg-[#6bb8f2] hover:bg-[#5577E0] text-white px-6 py-4 rounded-full shadow-2xl flex items-center space-x-2 transition-all duration-300 hover:scale-105"
        data-testid="mobile-contact-button"
      >
        <Mail className="w-5 h-5" />
        <span className="font-semibold">Contact Us</span>
      </Link>
    </>
  );
};

export default ADHDAssessmentPage;
